import { useDetailFunnelHealth } from "@/services/dashboard/detailFunnel";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import { BarChart3 } from "lucide-react";
import { useMemo } from "react";
import Chart from "react-apexcharts";

interface Props {
  startDate?: Date | null;
  endDate?: Date | null;
  userId?: string;
  userSelected?: string;
  projectSelected?: string;
  type?: string;
  region?: string;
  cordination?: string;
  principal?: string;
  master?: string;
}

const COLORS = [
  "#1F3B4D",
  "#3A506B",
  "#264653",
  "#2A9D8F",
  "#8AB17D",
  "#4E9F3D",
  "#6C757D",
  "#7A3E48",
  "#5E548E",
  "#9D4EDD",
  "#E76F51",
  "#FFB703",
  "#FB8500",
  "#F4A261",
  "#E9C46A",
  "#D4A373",
  "#8D99AE",
  "#556B2F",
  "#3C6E71",
  "#6D597A",
  "#2B2D42",
  "#4A4E69",
  "#1D3557",
  "#457B9D",
  "#81B29A",
  "#F7A072",
  "#C8B6A6",
  "#B5838D",
  "#B56576",
  "#355070",
];

function transformFunnelData(
  rawData: {
    statusName: string;
    campaign: string;
    region: string;
    reached: number;
    order: string;
  }[]
) {
  const groupedByStatus: Record<string, any> = {};

  rawData.forEach((item) => {
    const status = item.statusName;
    const key = `${item.campaign} - ${item.region}`;
    if (!groupedByStatus[status]) {
      groupedByStatus[status] = { statusName: status, order: item.order };
    }
    groupedByStatus[status][key] = item.reached;
  });

  return Object.values(groupedByStatus).sort(
    (a: any, b: any) => a.order - b.order
  );
}

export default function FunnelStackedChart({
  startDate,
  endDate,
  userId,
  userSelected,
  projectSelected,
  type,
  region,
  cordination,
  principal,
  master,
}: Props) {
  const filters = useMemo(
    () => ({
      startDate,
      endDate,
      userId,
      userSelected,
      projectSelected,
      type,
      region,
      cordination,
      principal,
      master,
    }),
    [
      startDate,
      endDate,
      userId,
      userSelected,
      projectSelected,
      type,
      region,
      cordination,
      principal,
      master,
    ]
  );

  const { data, isLoading, isError, error } = useDetailFunnelHealth(filters);

  const chartData = useMemo(() => {
    if (!data) return [];
    return transformFunnelData(data);
  }, [data]);

  const categories = useMemo(
    () => chartData.map((item: any) => item.statusName),
    [chartData]
  );

  const keys = useMemo(() => {
    if (!chartData.length) return [];
    return Array.from(
      new Set(
        chartData.flatMap((obj) =>
          Object.keys(obj).filter((k) => k !== "statusName" && k !== "order")
        )
      )
    );
  }, [chartData]);

  const series = useMemo(() => {
    return keys.map((key) => ({
      name: key,
      data: chartData.map((item: any) => item[key] || 0),
    }));
  }, [keys, chartData]);

  if (!chartData.length) return null;

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            {error instanceof Error
              ? error.message
              : "Error al cargar los datos"}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Paper elevation={3}>
      <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ p: 2 }}>
        <BarChart3 size={24} />
        <Typography variant="h6">Seguimiento por región y campaña</Typography>
      </Box>

      <Chart
        type="bar"
        height={400}
        series={series}
        options={{
          chart: {
            stacked: true,
            toolbar: { show: false },
          },
          colors: COLORS,
          xaxis: {
            categories,
            labels: {
              rotate: -45,
              style: {
                fontSize: "12px",
              },
            },
          },
          yaxis: {
            title: {
              text: "Número de tratos",
            },
          },
          legend: {
            position: "bottom",
            horizontalAlign: "left",
          },
          tooltip: {
            shared: true,
            intersect: false,
          },
          plotOptions: {
            bar: {
              horizontal: false,
            },
          },
        }}
      />
    </Paper>
  );
}
